import classNames from "classnames";
import moment from "moment";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ViewProps } from "../ExecutiveDashboard";
import Hint from "../components/Hint";
import {
	Avatar,
	Card,
	CountBadge,
	Empty,
	ErrorState,
	HealthBadge,
	Kpi,
	Loading,
	ProgressBar,
	StagePill,
	StatCircle
} from "../components/Visuals";
import { area, date, NEXT_STEP, number, percent, plural, relativeDays } from "../format";
import { Health, ProjectRow, ProjectsResponse, RefurbishmentProgram } from "../types";
import { useDashboardData } from "../useDashboardData";
import { Select } from "./Overview";

type GroupBy = "asset" | "status" | "none";

interface Props extends ViewProps {
	category: "delivery" | "refurbishment";
}

const HEALTH_ORDER: Health[] = ["delayed", "at_risk", "on_track"];
const HEALTH_TITLES: { [key in Health]: string } = { delayed: "Delayed", at_risk: "At risk", on_track: "On track" };

const Projects = ({ category, asset, properties }: Props) => {
	const [year, setYear] = useState(new Date().getFullYear());
	const { data, isLoading, error } = useDashboardData<ProjectsResponse>("projects", {
		category,
		property_id: asset ? asset.id : undefined,
		year: category === "refurbishment" ? year : undefined
	});

	if (isLoading && !data) return <Loading />;
	if (error || !data) return <ErrorState />;

	const { summary } = data;
	const refurb = category === "refurbishment";
	const flagged = summary.at_risk + summary.delayed;

	return (
		<div className="ed-stack">
			{refurb && data.program ? (
				<ProgramCard program={data.program} active={summary.active} year={year} onYearChange={setYear} />
			) : (
				<Card
					eyebrow="Active projects by stage"
					tag={`${plural(summary.active, "active delivery project")}`}
					explain={{
						title: "Stage",
						lines: [
							"Design: before the Actual Move in Date on the Site Handover task.",
							"Fit-out: on or after the Actual Move in Date, and the unit has not opened yet.",
							"The agreed Site Handover due date does not change the stage.",
							NEXT_STEP.design,
							"Next from Fit-out: the store opens on the Date Tenant Opened for Trade, and the project leaves this list when it is closed."
						]
					}}
				>
					<div className="ed-stat-row ed-stat-row--center ed-stat-row--tall">
						<StatCircle
							value={number(summary.design)}
							caption={`${percent(summary.design, summary.active, 0)} of active`}
							label="Design Stage"
							sublabel="In design / awaiting approval"
							tone="design"
							size="lg"
							explain={{ title: "Design Stage", lines: [`${number(summary.design)} ÷ ${number(summary.active)} active projects`] }}
						/>
						<StatCircle
							value={number(summary.fitout)}
							caption={`${percent(summary.fitout, summary.active, 0)} of active`}
							label="Fit-out Stage"
							sublabel="On site"
							tone="fitout"
							size="lg"
							explain={{ title: "Fit-out Stage", lines: [`${number(summary.fitout)} ÷ ${number(summary.active)} active projects`] }}
						/>
						{summary.other > 0 && (
							<StatCircle
								value={number(summary.other)}
								caption={`${percent(summary.other, summary.active, 0)} of active`}
								label="Other"
								sublabel="Terminations & contractions"
								tone="neutral"
								size="lg"
								explain={{
									title: "Other projects",
									lines: ["Projects whose workflow has no design or fit-out (no Site Handover task).", "Shown with their amendment type as the stage."]
								}}
							/>
						)}
					</div>
				</Card>
			)}

			<div className="ed-kpis">
				{refurb && data.program ? (
					<React.Fragment>
						<Kpi
							label="Annual target"
							value={data.program.target === null ? <span className="ed-required">Data required</span> : number(data.program.target)}
							explain={{ title: "Annual target", lines: ["The client-nominated number of refurbishments for the year.", "Not supplied yet."] }}
						/>
						<Kpi
							label="Completed"
							dot="trading"
							value={number(data.program.completed)}
							valueTone="trading"
							explain={{ title: "Completed", lines: [`Major or Minor refurbishment projects closed in ${data.program.year}.`] }}
						/>
						<Kpi
							label="Remaining"
							dot="design"
							value={data.program.target === null ? <span className="ed-required">Data required</span> : number(data.program.target - data.program.completed)}
							explain={{ title: "Remaining", lines: ["Annual target − completed.", "Needs the annual target."] }}
						/>
						<Kpi
							label="At risk"
							dot="vacant"
							value={number(data.program.at_risk)}
							valueTone="vacant"
							explain={{ title: "At risk", lines: ["Active refurbishments with an SLA task at risk or breached."] }}
						/>
					</React.Fragment>
				) : (
					<React.Fragment>
						<Kpi
							label="Active projects"
							value={number(summary.active)}
							explain={{ title: "Active projects", lines: ["Scheduled projects that are not yet completed (closed).", "Pending projects that haven't been scheduled are not included."] }}
						/>
						<Kpi
							label="On track"
							dot="trading"
							value={number(summary.on_track)}
							valueTone="trading"
							explain={{ title: "On track", lines: ["No SLA task is overdue.", `${number(summary.on_track)} of ${number(summary.active)} active`] }}
						/>
						<Kpi
							label="At risk"
							dot="vacant"
							value={number(flagged)}
							valueTone="vacant"
							sub={summary.delayed > 0 ? `${number(summary.delayed)} delayed (SLA breached)` : undefined}
							explain={{
								title: "At risk",
								lines: [
									"Any project with an SLA-nominated task that is overdue, using the SLA Monitor's rules.",
									`${number(summary.at_risk)} at risk (overdue, not yet breached) + ${number(summary.delayed)} delayed (breached)`
								]
							}}
						/>
						<Kpi
							label="Opening in next 30 days"
							dot="design"
							value={number(summary.opening_next_30_days)}
							valueTone="design"
							explain={{ title: "Opening in next 30 days", lines: ["Not yet opened, with a target open date in the next 30 days.", "Target open: agreed opening date from Yardi."] }}
						/>
					</React.Fragment>
				)}
			</div>

			<ProjectTable rows={data.projects} today={data.as_of} showAssetFilter={!asset} properties={properties} />
		</div>
	);
};

interface ProgramProps {
	program: RefurbishmentProgram;
	active: number;
	year: number;
	onYearChange: (year: number) => void;
}

const ProgramCard = ({ program, active, year, onYearChange }: ProgramProps) => {
	const current = new Date().getFullYear();
	const years = [current, current - 1, current - 2, current - 3];
	const inProgress = program.design + program.fitout + program.in_progress_other;
	const total = program.target || program.completed + inProgress + program.not_started || 1;
	const segments = [
		{ key: "completed", value: program.completed, tone: "trading" },
		{ key: "design", value: program.design, tone: "design" },
		{ key: "fitout", value: program.fitout + program.in_progress_other, tone: "fitout" },
		{ key: "not-started", value: program.not_started, tone: "neutral" }
	];
	return (
		<Card
			eyebrow={`${program.year} refurbishment program`}
			title={
				program.target === null ? (
					<React.Fragment>
						{number(program.completed)} complete <span className="ed-muted">· annual target: </span>
						<span className="ed-required">Data required</span>
					</React.Fragment>
				) : (
					<React.Fragment>
						{number(program.completed)} of {number(program.target)} complete{" "}
						<span className="ed-muted">· {number(program.target - program.completed)} to go</span>
					</React.Fragment>
				)
			}
			subtitle={`${plural(active, "refurbishment")} in progress`}
			explain={{
				title: "Refurbishment program",
				lines: [
					"Refurbishments are Renewal amendments of type Major or Minor Refurbishment.",
					"The annual target is set by the client and has not been supplied yet."
				]
			}}
			actions={
				<label className="ed-select">
					<span className="ed-select__label">Year:</span>
					<select value={year} onChange={(event) => onYearChange(Number(event.target.value))}>
						{years.map((option) => (
							<option key={option} value={option}>
								{option}
							</option>
						))}
					</select>
				</label>
			}
		>
			<div className="ed-segments" aria-hidden="true">
				{segments.map((segment) =>
					segment.value > 0 ? (
						<span
							key={segment.key}
							className={`ed-segments__part ed-tone--${segment.tone}`}
							style={{ width: `${(segment.value / total) * 100}%` }}
						/>
					) : null
				)}
			</div>
			<div className="ed-stat-row">
				<StatCircle
					value={number(program.completed)}
					caption={program.target ? `${percent(program.completed, program.target, 0)} of target` : "Delivered"}
					label="Completed"
					sublabel={`Closed in ${program.year}`}
					tone="trading"
					explain={{ title: "Completed", lines: [`Refurbishment projects closed during ${program.year}.`] }}
				/>
				<StatCircle
					value={number(program.design)}
					caption="Design / approval"
					label="In Design"
					tone="design"
					explain={{ title: "In Design", lines: ["Active refurbishments before their Actual Move in Date.", NEXT_STEP.design] }}
				/>
				<StatCircle
					value={number(program.fitout + program.in_progress_other)}
					caption="On site"
					label="In Fit-out"
					tone="fitout"
					explain={{ title: "In Fit-out", lines: ["Active refurbishments on or after their Actual Move in Date.", "Next: complete when the project is closed."] }}
				/>
				<StatCircle
					value={number(program.not_started)}
					caption="Yet to mobilise"
					label="Not Started"
					tone="neutral"
					explain={{ title: "Not Started", lines: ["Pending refurbishment projects, waiting to be scheduled."] }}
				/>
			</div>
		</Card>
	);
};

interface TableProps {
	rows: ProjectRow[];
	today: string;
	showAssetFilter: boolean;
	properties: ViewProps["properties"];
}

const ProjectTable = ({ rows, today, showAssetFilter, properties }: TableProps) => {
	const [query, setQuery] = useState("");
	const [groupBy, setGroupBy] = useState<GroupBy>("asset");
	const [assetFilter, setAssetFilter] = useState("");
	const [healthFilter, setHealthFilter] = useState("");
	const [collapsed, setCollapsed] = useState<{ [key: string]: boolean }>({});

	const needle = query.trim().toLowerCase();
	const filtered = rows.filter((row) => {
		if (assetFilter && (!row.property || row.property.id !== assetFilter)) return false;
		if (healthFilter && row.health !== healthFilter) return false;
		if (!needle) return true;
		const haystack = [row.tenant_name, row.name, row.units.join(" "), row.lead ? row.lead.name : "", row.type_label]
			.join(" ")
			.toLowerCase();
		return haystack.includes(needle);
	});

	const groups = buildGroups(filtered, groupBy, properties);
	const collapsedByDefault = groupBy === "asset";
	const toggle = (key: string) => setCollapsed((state) => ({ ...state, [key]: !isCollapsed(state, key, collapsedByDefault) }));

	return (
		<React.Fragment>
			<div className="ed-toolbar">
				<input
					className="ed-search"
					type="search"
					placeholder="Search project, tenant, unit, lead…"
					value={query}
					onChange={(event) => setQuery(event.target.value)}
				/>
				<div className="ed-segmented" role="group" aria-label="Group by">
					<span className="ed-segmented__label">Group by</span>
					{(["asset", "status", "none"] as GroupBy[]).map((option) => (
						<button
							key={option}
							type="button"
							className={classNames("ed-segmented__option", { "ed-segmented__option--active": groupBy === option })}
							onClick={() => setGroupBy(option)}
						>
							{option === "asset" ? "Asset" : option === "status" ? "Status" : "None"}
						</button>
					))}
				</div>
				{showAssetFilter && (
					<Select
						label="Asset"
						value={assetFilter}
						options={properties.map((property) => property.id)}
						format={(id) => {
							const match = properties.find((property) => property.id === id);
							return match ? match.short_name : id;
						}}
						onChange={setAssetFilter}
					/>
				)}
				<Select
					label="Health"
					value={healthFilter}
					options={HEALTH_ORDER}
					format={(value) => HEALTH_TITLES[value as Health]}
					onChange={setHealthFilter}
				/>
			</div>

			<section className="ed-card ed-card--flush">
				{filtered.length === 0 ? (
					<Empty>No projects match.</Empty>
				) : (
					<div className="ed-table-wrap">
						<table className="ed-table ed-table--projects">
							<thead>
								<tr>
									<th>Tenant / Unit</th>
									<th>Stage</th>
									<th>Handover</th>
									<th>Target open</th>
									<th>Progress</th>
									<th>Health</th>
									<th>Lead</th>
								</tr>
							</thead>
							{groups.map((group) => {
								const hidden = group.key !== "all" && isCollapsed(collapsed, group.key, collapsedByDefault);
								return (
									<tbody key={group.key}>
										{group.key !== "all" && (
											<tr className="ed-group" onClick={() => toggle(group.key)}>
												<td colSpan={4}>
													<span className={classNames("ed-group__caret", { "ed-group__caret--open": !hidden })}>▶</span>
													<span className={`ed-group__bar ed-tone--${group.tone}`} />
													<span className="ed-group__title">{group.title}</span>
													{group.subtitle && <span className="ed-subtext ed-group__subtitle">{group.subtitle}</span>}
												</td>
												<td colSpan={3} className="ed-group__summary">
													<span className="ed-muted">{plural(group.rows.length, "project")}</span>
													<CountBadge tone="on_track">{count(group.rows, "on_track")} on track</CountBadge>
													<CountBadge tone="at_risk">{count(group.rows, "at_risk") + count(group.rows, "delayed")} at risk</CountBadge>
												</td>
											</tr>
										)}
										{!hidden && group.rows.map((row) => <ProjectLine key={row.id} row={row} today={today} />)}
									</tbody>
								);
							})}
						</table>
					</div>
				)}
			</section>
		</React.Fragment>
	);
};

const ProjectLine = ({ row, today }: { row: ProjectRow; today: string }) => {
	const handoverPast = !!row.handover_date && moment(row.handover_date).isBefore(moment(today), "day");
	const healthExplain = {
		title: HEALTH_TITLES[row.health],
		lines: [
			"Rolled up from the project's SLA-nominated tasks, using the SLA Monitor's rules.",
			`Breached: ${row.sla_counts.breached || 0} · At risk: ${row.sla_counts.at_risk || 0} · Upcoming: ${row.sla_counts.upcoming || 0}`,
			row.overdue_days ? `Most overdue SLA task: ${row.overdue_days} days past due.` : null
		]
	};
	return (
		<tr>
			<td>
				<Link to={`/projects/${row.id}`} className="ed-link ed-link--plain ed-strong">
					{row.tenant_name}
				</Link>
				<div className="ed-subtext">
					{[
						row.units.join(", "),
						row.gla && (row.gla.sqft > 0 || row.gla.sqm > 0) ? area(row.gla) : null,
						row.property ? row.property.short_name : null
					]
						.filter(Boolean)
						.join(" · ")}
				</div>
			</td>
			<td>
				<Hint explain={{ lines: [`Type: ${row.type_label}`, row.stage === "other" ? "No design or fit-out in this workflow." : row.handover_actual ? `Actual Move in Date recorded: ${date(row.handover_date)}` : `Actual Move in Date not recorded yet (agreed ${date(row.handover_date)})`, projectNextStep(row)] }}>
					<StagePill stage={row.stage} label={row.stage_label} />
				</Hint>
			</td>
			<td>
				<Hint explain={{ lines: [row.handover_actual ? "Actual handover date." : "Agreed Site Handover date (actual not recorded yet)."] }}>
					<div className={classNames({ "ed-text--vacant": handoverPast && !row.handover_actual })}>{date(row.handover_date, "DD MMM")}</div>
					<div className="ed-subtext">{relativeDays(row.handover_date, today)}</div>
				</Hint>
			</td>
			<td>
				<Hint explain={{ lines: [row.target_open_actual ? "Actual opening date." : "Agreed opening date from Yardi."] }}>
					<div>{date(row.target_open_date, "DD MMM")}</div>
					<div className="ed-subtext">{relativeDays(row.target_open_date, today)}</div>
				</Hint>
			</td>
			<td>
				<Hint explain={{ lines: [`${row.progress_detail.closed} of ${row.progress_detail.total} tasks closed = ${row.progress}%`] }}>
					<ProgressBar value={row.progress} tone={row.health} />
				</Hint>
			</td>
			<td>
				<HealthBadge health={row.health} days={row.overdue_days} explain={healthExplain} />
			</td>
			<td>
				{row.lead ? (
					<Hint align="right" explain={{ lines: ["Project administrator.", row.other_leads ? `${row.other_leads} more administrators on this project.` : null] }}>
						<Avatar initials={row.lead.initials} name={row.lead.name} extra={row.other_leads} />
					</Hint>
				) : (
					<span className="ed-muted">–</span>
				)}
			</td>
		</tr>
	);
};

interface Group {
	key: string;
	title: string;
	subtitle?: string | null;
	tone: string;
	rows: ProjectRow[];
}

const buildGroups = (rows: ProjectRow[], groupBy: GroupBy, properties: ViewProps["properties"]): Group[] => {
	if (groupBy === "none") return [{ key: "all", title: "", tone: "design", rows }];
	if (groupBy === "status") {
		return HEALTH_ORDER.map((health) => ({
			key: health,
			title: HEALTH_TITLES[health],
			tone: health === "on_track" ? "trading" : "vacant",
			rows: rows.filter((row) => row.health === health)
		})).filter((group) => group.rows.length > 0);
	}
	return properties
		.map((property) => ({
			key: property.id,
			title: property.name,
			subtitle: property.location,
			tone: "design",
			rows: rows.filter((row) => row.property && row.property.id === property.id)
		}))
		.filter((group) => group.rows.length > 0);
};

const projectNextStep = (row: ProjectRow) => {
	if (row.stage === "design") return NEXT_STEP.design;
	if (row.stage === "fitout" && !row.refurbishment) return "Next: the store opens on the Date Tenant Opened for Trade recorded on the Opening Certificate task. The project leaves this list when it is closed.";
	return "Next: leaves this list when the project is closed.";
};

const isCollapsed = (state: { [key: string]: boolean }, key: string, byDefault: boolean) =>
	key in state ? state[key] : byDefault;

const count = (rows: ProjectRow[], health: Health) => rows.filter((row) => row.health === health).length;

export default Projects;
